import { useRef, useCallback } from 'react';
import { clsx } from 'clsx';

/* ── Types ────────────────────────────────────────────────────────── */

export interface Tab {
  key: string;
  label: string;
  count?: number;
}

export interface TabBarProps {
  tabs: Tab[];
  active: string;
  onChange: (key: string) => void;
  /** @default 'pills' */
  variant?: 'pills' | 'underline';
  /** @default 'md' */
  size?: 'sm' | 'md';
  /** Stretch tabs to fill the container equally */
  fullWidth?: boolean;
  className?: string;
}

/* ── Component ────────────────────────────────────────────────────── */

/**
 * TabBar — shared tab navigation component.
 *
 * Two visual variants:
 *   `pills`     — rounded background toggle (PersonalityCard style)
 *   `underline` — border-bottom indicator (Settings / ClientDetail style)
 *
 * Sizes:
 *   `sm`  — text-2xs (compact areas, cards)
 *   `md`  — text-xs  (page-level tabs)
 *
 * Includes full a11y: role="tablist" / role="tab", aria-selected,
 * and arrow-key navigation.
 */
export function TabBar({
  tabs,
  active,
  onChange,
  variant = 'pills',
  size = 'md',
  fullWidth = false,
  className,
}: TabBarProps) {
  const listRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const idx = tabs.findIndex((t) => t.key === active);
      let next = idx;

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        next = (idx + 1) % tabs.length;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        next = (idx - 1 + tabs.length) % tabs.length;
      } else if (e.key === 'Home') {
        e.preventDefault();
        next = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        next = tabs.length - 1;
      } else {
        return;
      }

      onChange(tabs[next]!.key);

      // Move focus to the newly-active tab button
      const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
      buttons?.[next]?.focus();
    },
    [tabs, active, onChange],
  );

  const isUnderline = variant === 'underline';
  const textSize = size === 'sm' ? 'text-2xs' : 'text-xs';

  return (
    <div
      ref={listRef}
      role="tablist"
      onKeyDown={handleKeyDown}
      className={clsx(
        // Shared
        'flex',
        // Variant-specific container
        isUnderline
          ? 'gap-1 border-b border-border overflow-x-auto'
          : 'gap-1 rounded-lg bg-surface-1 p-0.5',
        fullWidth && 'w-full',
        className,
      )}
    >
      {tabs.map((tab) => {
        const isActive = tab.key === active;
        return (
          <button
            key={tab.key}
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.key)}
            className={clsx(
              // Shared
              'relative whitespace-nowrap font-medium transition-all outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50',
              textSize,
              fullWidth && 'flex-1 text-center',
              // Variant-specific tab
              isUnderline
                ? clsx(
                    'px-4 py-2.5 shrink-0',
                    isActive ? 'text-primary' : 'text-ink-dim hover:text-ink',
                  )
                : clsx(
                    'rounded-md px-3 py-1.5',
                    isActive
                      ? 'bg-surface-2 text-ink shadow-xs'
                      : 'text-ink-dim hover:text-ink',
                  ),
            )}
          >
            <span className="inline-flex items-center gap-1.5">
              {tab.label}
              {tab.count != null && (
                <span
                  className={clsx(
                    'inline-flex items-center justify-center rounded-full px-1.5 min-w-4.5 h-4.5 text-2xs font-semibold tabular-nums',
                    isActive
                      ? 'bg-primary/15 text-primary'
                      : 'bg-surface-2 text-ink-dim',
                  )}
                >
                  {tab.count}
                </span>
              )}
            </span>

            {/* Underline active indicator */}
            {isUnderline && isActive && (
              <div className="absolute inset-x-0 bottom-0 h-[2px] bg-primary rounded-t-full transition-all" />
            )}
          </button>
        );
      })}
    </div>
  );
}
